import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('teamcraft_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setLoading(false);
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await axios.get(`${API}/auth/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser({ email: response.data.email, role: response.data.role });
    } catch (error) {
      localStorage.removeItem('teamcraft_token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token } = response.data;
    localStorage.setItem('teamcraft_token', access_token);
    setToken(access_token);
    
    // Verify token and set user immediately to avoid race condition with navigation
    try {
      const verifyResponse = await axios.get(`${API}/auth/verify`, {
        headers: { Authorization: `Bearer ${access_token}` }
      });
      setUser({ email: verifyResponse.data.email, role: verifyResponse.data.role });
    } catch (error) {
      // If verification fails, clean up and throw
      localStorage.removeItem('teamcraft_token');
      setToken(null);
      throw error;
    }
    
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('teamcraft_token');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
